<template>
  <button
    v-if="$variable('settings.wishlistEnable') && product"
    class="wishlist-button wk-button"
    :class="`wishlist-button--${template}`"
    type="button"
    :data-product-handle="product?.handle"
    :data-variant-id="variant?.id"
    @click.prevent="handleClick"
  >
    <span class="wishlist-button__wishlist-icon">
      <span
        class="visually-hidden"
        v-text="$string('product.product_card.add_to_wishlist')"
      />

      <icon-wishlist />
    </span>

    <span
      class="wishlist-button__wishlist-icon-filled"
      :class="$class('hidden')"
    >
      <span
        class="visually-hidden"
        v-text="$string('product.product_card.remove_from_wishlist')"
      />

      <icon-wishlist-fill />
    </span>
  </button>
</template>

<script>

/**
 * Vue: Wishlist button (wishlist-button)
 * -----------------------------------------------------------------------------
 * Button to add/remove product from wishlist.
 *
 * @param {Boolean|Object} product - Product object, false shows loading state.
 * @param {String} template - `card`, `form`, or `product`.
 * @param {Boolean|Object} variant - Specified or default variant.
 *
 */
import IconWishlist from '~icons/general/wishlist.svg'
import IconWishlistFill from '~icons/general/wishlist-fill.svg'

export default {
  name: 'WishlistButton',

  components: {
    IconWishlist,
    IconWishlistFill,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    template: {
      type: String,
      default: 'card',
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
  },

  data() {
    return {
      key: 'cnvs-wishlist-data',
    }
  },

  computed: {

    /**
     * Get the additional promo-tag for a product.
     * - If the product is a Collectors Exclusive, at that string from
     *   theme-strings
     * - Promo tag data from product metafields
     * @returns {String}
     */
    promoTag() {
      if (
        !this.product?.promoTag &&
        !this.product?.limits?.collectors
      ) {
        return ''
      }

      return this.product?.limits?.collectors
        ? this.$string('product.collectors_exclusive_promo_tag')
        : this.product?.promoTag
    },
  },

  methods: {

    /**
     * Save product data to localStorage.
     * - Promo tag and badge data not available in wishlist.
     */
    handleClick() {
      const variantId = this.variant?.id

      const details = {
        productBadges: this.product?.badges || [],
        promoTag: this.promoTag,
      }

      const data = {
        [variantId]: details,
      }

      const storage = localStorage.getItem(this.key)

      /**
       * If storage doesn't exist then write directly.
       */
      if (!storage) {
        localStorage.setItem(this.key, JSON.stringify(data))
        return
      }

      /**
       * If it does then update with new data.
       */
      const json = JSON.parse(storage)
      json[variantId] = details
      localStorage.setItem(this.key, JSON.stringify(json))
    },
  },
}
</script>

<style lang="scss">
@import './wishlist-button';
</style>
